import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";
import { resolveCurrentUserId } from "@/lib/articulos.server";
import { SEO_ORIGIN } from "@/lib/seo";
import type { Json } from "@/integrations/supabase/types";
import type { Evidencia } from "@/lib/notas-encargo.server";

/**
 * Notas de encargo con firma electrónica simple, sin proveedor externo:
 * el agente la envía desde la captación, el propietario la lee en
 * /firmar/:token, confirma su identidad con un código que recibe por email y
 * dibuja su firma. El PDF firmado se archiva como nota_encargo de la captación.
 */

async function servicio() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

const MAX_CODIGOS = 5;
const MAX_INTENTOS = 5;
const CODIGO_MIN = 15;

type Gestion = {
  id: string;
  agencia_id: string;
  anuncio_id: string | null;
  anuncio_snapshot: Record<string, unknown> | null;
  tipo: string;
  agencia_anunciante_id: string | null;
};

async function assertGestion(userId: string, gestionId: string): Promise<Gestion> {
  const sb = await servicio();
  const [{ data: g }, { data: roles }, { data: mems }] = await Promise.all([
    sb
      .from("gestiones_anuncio")
      .select("id, agencia_id, anuncio_id, anuncio_snapshot, tipo, agencia_anunciante_id")
      .eq("id", gestionId)
      .maybeSingle(),
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("agencia_miembros").select("agencia_id, rol").eq("user_id", userId),
  ]);
  if (!g) throw new Error("Captación no encontrada");
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  const isStaff = rs.includes("admin") || rs.includes("customer_service") || rs.includes("agente");
  const m = ((mems ?? []) as Array<{ agencia_id: string; rol: string }>).find((x) => x.agencia_id === g.agencia_id);
  if (!isStaff && !m) throw new Error("No autorizado");
  return g as unknown as Gestion;
}

const nombreDe = (c: { nombre?: string | null; apellidos?: string | null } | null | undefined) =>
  c ? [c.nombre, c.apellidos].filter(Boolean).join(" ").trim() : "";

function nuevoToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function evidencia(evento: string, detalle?: string) {
  let ip: string | null = null;
  let ua: string | null = null;
  try {
    ip = getRequestIP({ xForwardedFor: true }) ?? null;
    ua = getRequestHeader("user-agent") ?? null;
  } catch {
    /* fuera de una petición */
  }
  return { evento, at: new Date().toISOString(), ip, ua, ...(detalle ? { detalle } : {}) };
}

/** Datos del local y del propietario para rellenar la nota. */
async function datosLocal(g: Gestion) {
  const sb = await servicio();
  const snap = (g.anuncio_snapshot ?? {}) as Record<string, unknown>;
  let anuncio: Record<string, unknown> | null = null;
  type Prop = { id: string; nombre: string | null; apellidos: string | null; email: string | null };
  let propietario: Prop | null = null;
  if (g.anuncio_id) {
    const [{ data: a }, { data: ac }] = await Promise.all([
      sb
        .from("anuncios")
        .select("referencia, alias, direccion_completa, cp, precio_traspaso, precio_alquiler, precio_venta, operacion_id")
        .eq("id", g.anuncio_id)
        .maybeSingle(),
      g.tipo === "captacion" && !g.agencia_anunciante_id
        ? sb
            .from("anuncio_contactos")
            .select("rol, created_at, contactos(id, nombre, apellidos, email)")
            .eq("anuncio_id", g.anuncio_id)
            .order("created_at")
        : Promise.resolve({ data: [] }),
    ]);
    anuncio = (a as Record<string, unknown> | null) ?? null;
    const filas = ((ac ?? []) as unknown as Array<{ rol: string; contactos: Prop | null }>).sort(
      (x, y) => Number(y.rol === "anunciante") - Number(x.rol === "anunciante"),
    );
    propietario = filas[0]?.contactos ?? null;
  }
  const src = { ...snap, ...(anuncio ?? {}) } as Record<string, unknown>;
  const local =
    (src.direccion_completa as string | undefined) ||
    (src.alias as string | undefined) ||
    (src.cp ? `Local en CP ${src.cp}` : "el local");
  const operacion: "traspaso" | "alquiler" | "venta" = src.precio_traspaso
    ? "traspaso"
    : src.precio_venta && !src.precio_alquiler
      ? "venta"
      : src.precio_alquiler
        ? "alquiler"
        : "traspaso";
  const precio =
    (operacion === "traspaso"
      ? (src.precio_traspaso as number | null)
      : operacion === "venta"
        ? (src.precio_venta as number | null)
        : (src.precio_alquiler as number | null)) ?? null;
  return {
    referencia: (src.referencia as string | null) ?? null,
    local,
    operacion,
    precio,
    propietario,
  };
}

const ESTADO_LABEL = { enviada: "Enviada", vista: "Vista", firmada: "Firmada", cancelada: "Cancelada" } as const;
export type NotaEstado = keyof typeof ESTADO_LABEL;

/** Lo necesario para preparar una nota: datos sugeridos y notas ya enviadas. */
export const getNotasEncargoGestion = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ gestion_id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const g = await assertGestion(userId, data.gestion_id);
    const sb = await servicio();
    const [datos, { data: notas }] = await Promise.all([
      datosLocal(g),
      sb
        .from("notas_encargo")
        .select(
          "id, token, estado, firmante_nombre, firmante_email, enviada_at, vista_at, firmada_at, cancelada_at, expires_at, documento_id, condiciones",
        )
        .eq("gestion_anuncio_id", g.id)
        .order("created_at", { ascending: false }),
    ]);
    return {
      sugerido: {
        local: datos.local,
        referencia: datos.referencia,
        operacion: datos.operacion,
        precio: datos.precio,
        firmante: datos.propietario
          ? {
              nombre: nombreDe(datos.propietario),
              email: datos.propietario.email ?? "",
            }
          : { nombre: "", email: "" },
      },
      notas: ((notas ?? []) as Array<Record<string, unknown>>).map((n) => ({
        id: n.id as string,
        estado: n.estado as NotaEstado,
        firmante_nombre: n.firmante_nombre as string,
        firmante_email: n.firmante_email as string,
        enviada_at: n.enviada_at as string,
        vista_at: (n.vista_at as string | null) ?? null,
        firmada_at: (n.firmada_at as string | null) ?? null,
        cancelada_at: (n.cancelada_at as string | null) ?? null,
        caducada: n.estado !== "firmada" && new Date(n.expires_at as string).getTime() < Date.now(),
        url: `${SEO_ORIGIN}/firmar/${n.token as string}`,
        documento_id: (n.documento_id as string | null) ?? null,
      })),
    };
  });

const CondicionesSchema = z.object({
  operacion: z.enum(["traspaso", "alquiler", "venta"]),
  precio: z.number().min(0).max(1e9).nullable(),
  honorarios_tipo: z.enum(["porcentaje", "fijo"]),
  honorarios_valor: z.number().min(0).max(1e8),
  exclusiva: z.boolean(),
  duracion_meses: z.number().int().min(1).max(60),
  observaciones: z.string().trim().max(1500).nullable().optional(),
});

export const enviarNotaEncargo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        gestion_id: z.string().uuid(),
        firmante: z.object({
          nombre: z.string().trim().min(2).max(200),
          email: z.string().trim().email().max(200),
          dni: z.string().trim().max(30).nullable().optional(),
        }),
        condiciones: CondicionesSchema,
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const g = await assertGestion(userId, data.gestion_id);
    const sb = await servicio();
    const { construirContenido, sha256Hex } = await import("@/lib/notas-encargo.server");
    const [datos, { data: agente }, { data: agencia }] = await Promise.all([
      datosLocal(g),
      sb.from("contactos").select("nombre, apellidos").eq("user_id", userId).maybeSingle(),
      sb.from("agencias").select("nombre").eq("id", g.agencia_id).maybeSingle(),
    ]);
    const agenteNombre = nombreDe(agente) || "el agente";
    const agenciaNombre = (agencia as { nombre?: string } | null)?.nombre ?? "la agencia";
    const firmante = { nombre: data.firmante.nombre, email: data.firmante.email.toLowerCase(), dni: data.firmante.dni || null };
    const contenido = construirContenido(data.condiciones, {
      fecha: new Date(),
      referencia: datos.referencia,
      local: datos.local,
      firmante,
      agencia: agenciaNombre,
      agente: agenteNombre,
    });
    const hash = await sha256Hex(contenido);

    const { upsertContactoByIdentity } = await import("@/lib/contactos-upsert.server");
    const [nombre, ...resto] = firmante.nombre.split(/\s+/);
    const contacto = await upsertContactoByIdentity(sb, {
      nombre,
      apellidos: resto.join(" ") || null,
      email: firmante.email,
      agenciaId: g.agencia_id,
      createdBy: userId,
      origen: "manual",
    });

    const token = nuevoToken();
    const { data: nota, error } = await sb
      .from("notas_encargo")
      .insert({
        gestion_anuncio_id: g.id,
        agencia_id: g.agencia_id,
        agente_id: userId,
        contacto_id: contacto.id,
        firmante_nombre: firmante.nombre,
        firmante_email: firmante.email,
        firmante_dni: firmante.dni,
        condiciones: data.condiciones,
        contenido,
        hash_documento: hash,
        token,
        evidencias: [evidencia("Enviada para firmar", `por ${agenteNombre} a ${firmante.email}`)],
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    const url = `${SEO_ORIGIN}/firmar/${token}`;
    const { sendLoggedManagedEmail } = await import("@/lib/logged-email.server");
    await sendLoggedManagedEmail(sb as never, {
      contactId: contacto.id,
      to: firmante.email,
      actor: userId,
      tipo: "nota_encargo_firma",
      meta: { nota_encargo_id: nota.id, gestion_anuncio_id: g.id },
      draft: {
        key: "nota_encargo_firma",
        variables: {
          saludo: `Hola ${nombre},`,
          agente_nombre: agenteNombre,
          agencia_nombre: agenciaNombre,
          local: datos.local,
          firma_url: url,
        },
      },
    });
    await sb.from("gestion_anuncio_eventos").insert({
      gestion_anuncio_id: g.id,
      agencia_id: g.agencia_id,
      user_id: userId,
      tipo: "nota_encargo_enviada",
      titulo: `Nota de encargo enviada a ${firmante.nombre} para firmar`,
      metadata: { nota_encargo_id: nota.id },
    });
    return { ok: true as const, id: nota.id as string, url };
  });

export const cancelarNotaEncargo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = await servicio();
    const { data: n } = await sb
      .from("notas_encargo")
      .select("id, gestion_anuncio_id, estado, evidencias")
      .eq("id", data.id)
      .maybeSingle();
    if (!n) throw new Error("Nota no encontrada");
    await assertGestion(userId, n.gestion_anuncio_id);
    if (n.estado === "firmada") throw new Error("Ya está firmada: no se puede anular");
    const { error } = await sb
      .from("notas_encargo")
      .update({
        estado: "cancelada",
        cancelada_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        evidencias: [...((n.evidencias as Json[]) ?? []), evidencia("Anulada por la agencia")],
      })
      .eq("id", n.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Página pública de firma ─────────

const TokenInput = z.object({ token: z.string().trim().min(16).max(40).regex(/^[A-Za-z0-9_-]+$/) });

async function notaPorToken(token: string) {
  const sb = await servicio();
  const { data } = await sb.from("notas_encargo").select("*").eq("token", token).maybeSingle();
  return data as
    | (Record<string, unknown> & {
        id: string;
        estado: NotaEstado;
        evidencias: Json[];
        expires_at: string;
        firmante_email: string;
        firmante_nombre: string;
        firmante_dni: string | null;
        contenido: string;
        hash_documento: string;
        agencia_id: string;
        agente_id: string;
        gestion_anuncio_id: string;
        contacto_id: string | null;
        token: string;
        codigo_hash: string | null;
        codigo_expira: string | null;
        codigo_intentos: number;
        codigos_enviados: number;
        documento_id: string | null;
        firmada_at: string | null;
        condiciones: Record<string, unknown>;
      })
    | null;
}

const ocultarEmail = (e: string) => e.replace(/^(.{1,2})[^@]*(@.*)$/, (_m, a: string, b: string) => `${a}•••${b}`);

export const getNotaParaFirmar = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => TokenInput.parse(d))
  .handler(async ({ data }) => {
    const n = await notaPorToken(data.token);
    if (!n) return null;
    const sb = await servicio();
    const { data: ag } = await sb.from("agencias").select("nombre, logo_url").eq("id", n.agencia_id).maybeSingle();
    const caducada = n.estado !== "firmada" && new Date(n.expires_at).getTime() < Date.now();
    return {
      estado: n.estado,
      caducada,
      contenido: n.contenido,
      hash: n.hash_documento,
      firmante: n.firmante_nombre,
      emailOculto: ocultarEmail(n.firmante_email),
      firmadaAt: n.firmada_at,
      agencia: { nombre: (ag as { nombre?: string } | null)?.nombre ?? "", logo_url: (ag as { logo_url?: string | null } | null)?.logo_url ?? null },
    };
  });

/** La primera vez que se abre el enlace queda como «vista». */
export const registrarVistaNota = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => TokenInput.parse(d))
  .handler(async ({ data }) => {
    const n = await notaPorToken(data.token);
    if (!n || n.estado !== "enviada") return { ok: true };
    const sb = await servicio();
    const now = new Date().toISOString();
    await sb
      .from("notas_encargo")
      .update({ estado: "vista", vista_at: now, updated_at: now, evidencias: [...(n.evidencias ?? []), evidencia("Abierta por el firmante")] })
      .eq("id", n.id)
      .eq("estado", "enviada");
    return { ok: true };
  });

function assertFirmable(n: NonNullable<Awaited<ReturnType<typeof notaPorToken>>>) {
  if (n.estado === "firmada") throw new Error("Esta nota ya está firmada");
  if (n.estado === "cancelada") throw new Error("La agencia ha anulado esta nota");
  if (new Date(n.expires_at).getTime() < Date.now()) throw new Error("El enlace ha caducado: pide a la agencia uno nuevo");
}

export const enviarCodigoFirma = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => TokenInput.parse(d))
  .handler(async ({ data }) => {
    const n = await notaPorToken(data.token);
    if (!n) throw new Error("Enlace no válido");
    assertFirmable(n);
    if (n.codigos_enviados >= MAX_CODIGOS) throw new Error("Has pedido demasiados códigos. Escribe a la agencia para que te envíe un enlace nuevo.");
    const { sha256Hex } = await import("@/lib/notas-encargo.server");
    const num = crypto.getRandomValues(new Uint32Array(1))[0]! % 1_000_000;
    const codigo = String(num).padStart(6, "0");
    const sb = await servicio();
    const [{ data: agente }, { data: ag }] = await Promise.all([
      sb.from("contactos").select("nombre, apellidos").eq("user_id", n.agente_id).maybeSingle(),
      sb.from("agencias").select("nombre").eq("id", n.agencia_id).maybeSingle(),
    ]);
    const now = new Date();
    const { error } = await sb
      .from("notas_encargo")
      .update({
        codigo_hash: await sha256Hex(`${n.token}:${codigo}`),
        codigo_expira: new Date(now.getTime() + CODIGO_MIN * 60_000).toISOString(),
        codigo_intentos: 0,
        codigos_enviados: n.codigos_enviados + 1,
        updated_at: now.toISOString(),
        evidencias: [...(n.evidencias ?? []), evidencia("Código de firma enviado", ocultarEmail(n.firmante_email))],
      })
      .eq("id", n.id);
    if (error) throw new Error(error.message);
    const local = /local situado en (.+?)\.\n/.exec(n.contenido)?.[1] ?? "tu local";
    const { sendLoggedManagedEmail } = await import("@/lib/logged-email.server");
    if (n.contacto_id) {
      await sendLoggedManagedEmail(sb as never, {
        contactId: n.contacto_id,
        to: n.firmante_email,
        actor: n.agente_id,
        tipo: "nota_encargo_codigo",
        meta: { nota_encargo_id: n.id },
        draft: {
          key: "nota_encargo_codigo",
          variables: {
            codigo,
            local,
            agente_nombre: nombreDe(agente) || "Tu agente",
            agencia_nombre: (ag as { nombre?: string } | null)?.nombre ?? "",
          },
        },
      });
    } else {
      throw new Error("No se pudo enviar el código: falta el contacto");
    }
    return { ok: true as const, email: ocultarEmail(n.firmante_email) };
  });

export const firmarNotaEncargo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    TokenInput.extend({
      codigo: z.string().trim().regex(/^\d{6}$/, "El código tiene 6 cifras"),
      firma_png: z.string().min(100).max(700_000),
      acepto: z.literal(true),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const n = await notaPorToken(data.token);
    if (!n) throw new Error("Enlace no válido");
    assertFirmable(n);
    const sb = await servicio();
    const { sha256Hex, generarPdfFirmado, fechaLarga } = await import("@/lib/notas-encargo.server");
    if (!n.codigo_hash || !n.codigo_expira) throw new Error("Pide primero el código");
    if (new Date(n.codigo_expira).getTime() < Date.now()) throw new Error("El código ha caducado: pide otro");
    if (n.codigo_intentos >= MAX_INTENTOS) throw new Error("Demasiados intentos: pide otro código");
    if ((await sha256Hex(`${n.token}:${data.codigo}`)) !== n.codigo_hash) {
      await sb
        .from("notas_encargo")
        .update({ codigo_intentos: n.codigo_intentos + 1, updated_at: new Date().toISOString() })
        .eq("id", n.id);
      throw new Error(`Código incorrecto. Te quedan ${Math.max(0, MAX_INTENTOS - n.codigo_intentos - 1)} intentos.`);
    }
    // El texto no ha cambiado desde que se envió.
    if ((await sha256Hex(n.contenido)) !== n.hash_documento) throw new Error("El documento no coincide con el enviado");

    const b64 = data.firma_png.replace(/^data:image\/png;base64,/, "");
    const firmaPng = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    if (firmaPng[0] !== 0x89 || firmaPng[1] !== 0x50) throw new Error("La firma no es válida");

    const firmadaAt = new Date();
    const evidencias = [
      ...((n.evidencias ?? []) as Json[]),
      evidencia("Identidad confirmada con el código enviado por email"),
      evidencia("Firmada", "acepta el contenido íntegro"),
    ];
    const pdf = await generarPdfFirmado({
      contenido: n.contenido,
      hash: n.hash_documento,
      firmaPng,
      firmante: { nombre: n.firmante_nombre, email: n.firmante_email, dni: n.firmante_dni },
      firmadaAt,
      evidencias: evidencias as unknown as Evidencia[],
      token: n.token,
    });

    const docId = crypto.randomUUID();
    const BUCKET = "documentos";
    const firmaPath = `${n.agencia_id}/firmas/${n.id}.png`;
    const pdfPath = `${n.agencia_id}/${docId}.pdf`;
    const up1 = await sb.storage.from(BUCKET).upload(firmaPath, firmaPng, { contentType: "image/png", upsert: true });
    if (up1.error) throw new Error(up1.error.message);
    const up2 = await sb.storage.from(BUCKET).upload(pdfPath, pdf, { contentType: "application/pdf", upsert: false });
    if (up2.error) throw new Error(up2.error.message);
    const { error: eDoc } = await sb.from("documentos").insert({
      id: docId,
      titulo: `Nota de encargo firmada · ${n.firmante_nombre}`,
      extension: "pdf",
      mime_type: "application/pdf",
      tipo: "nota_encargo",
      storage_path: pdfPath,
      size_bytes: pdf.byteLength,
      agencia_id: n.agencia_id,
      gestion_anuncio_id: n.gestion_anuncio_id,
      uploaded_by: n.agente_id,
      fecha_emision: firmadaAt.toISOString().slice(0, 10),
    });
    if (eDoc) {
      await sb.storage.from(BUCKET).remove([pdfPath]).catch(() => {});
      throw new Error(eDoc.message);
    }
    const { data: actualizada, error } = await sb
      .from("notas_encargo")
      .update({
        estado: "firmada",
        firmada_at: firmadaAt.toISOString(),
        updated_at: firmadaAt.toISOString(),
        codigo_hash: null,
        firma_storage_path: firmaPath,
        documento_id: docId,
        evidencias,
      })
      .eq("id", n.id)
      .neq("estado", "firmada")
      .select("id");
    if (error) throw new Error(error.message);
    if (!actualizada?.length) throw new Error("Esta nota ya está firmada");

    await sb.from("gestion_anuncio_eventos").insert({
      gestion_anuncio_id: n.gestion_anuncio_id,
      agencia_id: n.agencia_id,
      user_id: null,
      tipo: "nota_encargo_firmada",
      titulo: `${n.firmante_nombre} firmó la nota de encargo`,
      metadata: { nota_encargo_id: n.id, documento_id: docId },
    });

    // Copia para el firmante (si falla el envío, la firma ya está hecha).
    try {
      if (n.contacto_id) {
        const [{ data: agente }, { data: ag }] = await Promise.all([
          sb.from("contactos").select("nombre, apellidos").eq("user_id", n.agente_id).maybeSingle(),
          sb.from("agencias").select("nombre").eq("id", n.agencia_id).maybeSingle(),
        ]);
        const { sendLoggedManagedEmail } = await import("@/lib/logged-email.server");
        await sendLoggedManagedEmail(sb as never, {
          contactId: n.contacto_id,
          to: n.firmante_email,
          actor: n.agente_id,
          tipo: "nota_encargo_firmada",
          meta: { nota_encargo_id: n.id, documento_id: docId },
          draft: {
            key: "nota_encargo_firmada",
            variables: {
              saludo: `Hola ${n.firmante_nombre.split(/\s+/)[0]},`,
              local: /local situado en (.+?)\.\n/.exec(n.contenido)?.[1] ?? "tu local",
              fecha_firma: fechaLarga(firmadaAt),
              firma_url: `${SEO_ORIGIN}/firmar/${n.token}`,
              agente_nombre: nombreDe(agente) || "Tu agente",
              agencia_nombre: (ag as { nombre?: string } | null)?.nombre ?? "",
            },
          },
        });
      }
    } catch (e) {
      console.error("[notas-encargo] copia al firmante", e);
    }
    return { ok: true as const };
  });

/** Enlace temporal al PDF firmado (para el firmante, desde su enlace). */
export const descargarNotaFirmada = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => TokenInput.parse(d))
  .handler(async ({ data }) => {
    const n = await notaPorToken(data.token);
    if (!n || n.estado !== "firmada" || !n.documento_id) throw new Error("La nota aún no está firmada");
    const sb = await servicio();
    const { data: doc } = await sb.from("documentos").select("storage_path").eq("id", n.documento_id).maybeSingle();
    if (!doc) throw new Error("Documento no encontrado");
    const { data: signed, error } = await sb.storage.from("documentos").createSignedUrl(doc.storage_path, 600);
    if (error) throw new Error(error.message);
    return { url: signed.signedUrl };
  });

/** Enlace temporal al PDF firmado, desde el CRM. */
export const urlNotaFirmadaAgente = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = await servicio();
    const { data: n } = await sb
      .from("notas_encargo")
      .select("gestion_anuncio_id, documento_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!n?.documento_id) throw new Error("La nota aún no está firmada");
    await assertGestion(userId, n.gestion_anuncio_id);
    const { data: doc } = await sb.from("documentos").select("storage_path").eq("id", n.documento_id).maybeSingle();
    if (!doc) throw new Error("Documento no encontrado");
    const { data: signed, error } = await sb.storage.from("documentos").createSignedUrl(doc.storage_path, 600);
    if (error) throw new Error(error.message);
    return { url: signed.signedUrl };
  });
